import React from "react";
import { createRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import App from "./App.tsx";
import "./index.css";
// GOOGIE-1 — the redesign's stylesheet. Every rule is scoped under
// `.googie`, so importing it changes nothing until the shell is on.
import "./styles/googie.css";
import { applyThemeMode, THEME_MODE_KEY, type ThemeMode } from "@/hooks/useThemeMode";
import { initObservability } from "@/lib/observability";
import { isOptionalImportError, optionalImportInFlight } from "@/lib/optionalImport";

// SCALE-2 — error tracking, initialised BEFORE createRoot so a crash
// during the very first render is still reported. No-ops (with one
// console line, never silently) when VITE_SENTRY_DSN is unset.
//
// HOTFIX-1 — belt to `observability.ts`'s braces. That module is now
// internally fail-safe (lazy SDK import, try/catch everywhere, no
// top-level await), but this call sits four lines above `createRoot`,
// so the one thing that must be true is that NOTHING it does can stop
// the render below from running. Hence the outer catch: error tracking
// is a diagnostic, and a diagnostic may never be the reason the app
// doesn't boot.
// DOMAIN-5 (incident fix) — the DOMAIN-4 www→bare-domain hop that used
// to live here caused an INFINITE redirect loop in production:
//   1. hosting canonicalises https://orbitstudiocrm.com/*  --308-->
//      https://www.orbitstudiocrm.com/*
//   2. this file saw a non-marketing path on `www` and did
//      location.replace('https://orbitstudiocrm.com' + path)
//   3. -> back to step 1, forever.
// Every in-flight request in the tab got aborted mid-navigation, which
// Safari surfaces as "cannot load X due to access control checks" /
// "TypeError: Load failed" — on same-origin .js.map files and on
// cross-origin backend calls alike. `www` IS the canonical serving
// host, so the app treats it as an app host and never redirects.


// DEPLOY-1 — self-heal after a mid-deploy refresh.
//
// The app deploys many times a day. A tab that refreshes while a
// deploy is landing (or that held a stale index.html) asks for hashed
// route chunks the CDN has already replaced -> the dynamic import
// fails, the route renders half-styled (missing lg: grids, chrome
// overlapping content), and it LOOKS like a layout bug. Vite fires
// `vite:preloadError` for exactly this; one hard reload picks up the
// fresh index.html and manifest. sessionStorage-guarded so a truly
// broken deploy degrades to the error state instead of a reload loop
// (the guard clears on the first successful load after recovery).
//
// NOVA-ENV-1 (Oct 12) — one exception, for imports that are allowed to
// fail. /nova/environment asks for the 3D Nova and the Earth on every
// load and falls back to stills without them; reloading because one of
// those chunks is unreachable would only ask again, and the guard above
// clears on every load, so it would never stop. Those imports go through
// `optionalImport` (src/lib/optionalImport.ts). Vite's event does not
// say WHICH import failed, so while one of them is in flight we let the
// rejection through and look again on the next tick: by then a failure
// that was theirs has reached their own `.catch` and been recorded, and
// anything else still gets the reload. With no optional import in
// flight — every other moment, on every other page — this handler does
// exactly what it did before.
const PRELOAD_RELOADED_KEY = "orbit.chunk_reloaded";
/** One hard reload for fresh chunks. False when that has already been tried. */
const reloadForFreshChunks = (): boolean => {
  try {
    if (sessionStorage.getItem(PRELOAD_RELOADED_KEY) === "1") return false; // already tried
    sessionStorage.setItem(PRELOAD_RELOADED_KEY, "1");
    window.location.reload();
    return true;
  } catch {
    return false; // storage unavailable — let the error surface normally
  }
};
window.addEventListener("vite:preloadError", (event) => {
  if (optionalImportInFlight()) {
    const failure = (event as Event & { payload?: unknown }).payload;
    window.setTimeout(() => {
      if (!isOptionalImportError(failure)) reloadForFreshChunks();
    }, 0);
    return;
  }
  if (reloadForFreshChunks()) event.preventDefault(); // we own recovery — stop the unhandled rejection
});
window.addEventListener("load", () => {
  try { sessionStorage.removeItem(PRELOAD_RELOADED_KEY); } catch { /* noop */ }
});

try {
  initObservability();
} catch (err) {
  console.warn('[observability] init threw — error reporting is OFF:', err);
}

// Wave 13a — Apply the persisted theme mode BEFORE React mounts
// so the first paint uses the right palette (no light→dark or
// dark→light flash).  Reads `orbit_theme_mode` from localStorage
// (default: dark) and syncs `<html>`:
//   • data-theme attribute — primary source of truth
//   • .dark class — kept for legacy consumers (next-themes, etc.)
//   • .new-shell class — added ONLY in dark mode so the cosmic
//     ambient body backdrop doesn't paint over the light canvas.
(() => {
  try {
    const stored = window.localStorage.getItem(THEME_MODE_KEY);
    const mode: ThemeMode = stored === 'light' ? 'light' : 'dark';
    applyThemeMode(mode);
  } catch {
    applyThemeMode('dark');
  }
})();

createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <HelmetProvider>
      <App />
    </HelmetProvider>
  </React.StrictMode>
);
